import './styles/styles.css';
import './i18n';
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import { Menu } from '@headlessui/react';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import Flag from 'react-flagkit';
import logo from '../src/tuutuum-logo.png';

import {
	get_data_by_token,
	send_location_data
} from "./tools-network";
import BearerMedicalDisplay from '../../../shared/components/BearerMedicalDisplay';

import config from './config';

const LanguageSwitcher = () => {
	const { i18n } = useTranslation();
	
	return (
		<Menu as="div" className="relative z-50">
			<Menu.Button
				className="p-2 rounded-full hover:bg-gray-100"
				title="Change Language"
			>
				<GlobeAltIcon className="h-7 w-7 text-gray-700" aria-hidden="true" />
			</Menu.Button>
			<Menu.Items className="absolute right-0 mt-2 w-52 origin-top-right bg-white border border-gray-300 divide-y divide-gray-100 rounded-md shadow-lg outline-none">
				<Menu.Item>
					{({ active }) => (
						<button
							className={`${
								active ? 'bg-blue text-black' : 'text-gray-900'
							} group flex rounded-md items-center w-full px-2 py-2 text-sm ${
								i18n.language === 'en' ? 'font-bold' : ''
							}`}
							onClick={() => i18n.changeLanguage('en')}
						>
							<Flag country="GB" size={20} className="mr-2" />
							English
						</button>
					)}
				</Menu.Item>
				<Menu.Item>
					{({ active }) => (
						<button
							className={`${
								active ? 'bg-blue text-black' : 'text-gray-900'
							} group flex rounded-md items-center w-full px-2 py-2 text-sm ${
								i18n.language === 'sv' ? 'font-bold' : ''
							}`}
							onClick={() => i18n.changeLanguage('sv')}
						>
							<Flag country="SE" size={20} className="mr-2" />
							Svenska
						</button>
					)}
				</Menu.Item>
			</Menu.Items>
		</Menu>
	);
};

const Unloaded = () => {
	const { t } = useTranslation();
	return <div className="flex w-full h-full items-center justify-center text-xl italic font-semibold text-cyan-800 loading-animation">{t('loading')}</div>;
}

const MedicalData = ({token, bearerName, isVerbal, diseases, birthYear, sex, bloodType, scanId, noSms}) => {
	const { t } = useTranslation();
	const [locationData, setLocationData]	= useState(null);

	const location_received = async (pos) => {
	    const loc = pos.coords.latitude + "," + pos.coords.longitude;

		// This triggers an update of the GUI
		await setLocationData(loc);

		// Check if we've already shared location for this token in this session
		const locationSessionKey = `tuutuum_location_${token}`;
		const alreadySharedLocation = sessionStorage.getItem(locationSessionKey) === 'true';
		
		// Add no_sms if location already shared (prevents duplicate location SMS on reload)
		const noSmsParam = alreadySharedLocation || noSms;

		// Update server
		await send_location_data(token, scanId, loc, noSmsParam);
		
		// Mark location as shared for this session
		sessionStorage.setItem(locationSessionKey, 'true');
	};

	// Ask for location data (once!)
	useEffect(() => {
		if(navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(location_received);
		} else {
			console.log("GEOLOCATION NOT SUPPORTED BY WEB BROSWER!");
		}
	}, []);
	
	return (
		<>
			<div className="flex items-center justify-center">
				<div className="flex flex-col neumorp items-center justify-center space-y-5 bg-cyan-50 px-6 py-10 sm:p-10 rounded-lg shadow-lg w-full max-w-xs sm:max-w-sm border-2 border-cyan-200">
					{/* Logo and Language Switcher */}
					<div className="flex items-center justify-between w-full">
						<img src={logo} alt="Tuutuum Logo" className="block h-7 w-auto"/>
						<LanguageSwitcher />
					</div>
					
					<h3 className="text-sm font-semibold text-gray-700 italic self-start w-full">
						{t('preview_notice')}
					</h3>
					
					<div className="bg-white rounded-lg p-4 shadow-sm w-full text-sm">
						<BearerMedicalDisplay
							t={t}
							isVerbal={isVerbal}
							diseases={diseases}
							birthYear={birthYear}
							sex={sex}
							bloodType={bloodType}
						/>
					</div>

					<div
						hidden={locationData || !navigator.geolocation}
						className="mt-4">
						<h3 className="!text-red-500 font-semibold">
							{t('share_location')}
						</h3>
					</div>
					<div
						hidden={!locationData}
						className="mt-4">
						<h3 className="!text-green-500 font-semibold">
							{t('location_shared')}
						</h3>
					</div>
				</div>
			</div>
		</>
	);
}

const UninitializedToken = ({token}) => {
	const { t, i18n } = useTranslation();
	// Try to get language from cookie (shared with admin site on .tuutuum.se domain)
	const getCookie = (name) => {
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) return parts.pop().split(';').shift();
	};
	const lang = getCookie('i18next') || i18n.language || 'en';
	const link = config.ADMIN_REROUTE + '?pending_token=' + token + '&lng=' + lang;
	return (
		<>
			<div className="flex items-center justify-center">
				<div className="flex flex-col neumorp items-center justify-center space-y-3 bg-white px-6 py-10 sm:p-10 rounded-lg shadow-lg w-full max-w-xs sm:max-w-sm">
					{/* Logo and Language Switcher */}
					<div className="flex items-center justify-between w-full">
						<img src={logo} alt="Tuutuum Logo" className="block h-7 w-auto"/>
						<LanguageSwitcher />
					</div>
					<div className="mx-auto max-w-6xl py-1">
						<a 
							href={link}
							className="btn-main"
						>
								{t('register_wristband')}
						</a>
					</div>
				</div>
			</div>
		</>
	);
}

const BadToken = ({token}) => {
	const { t } = useTranslation();
	return <div className="flex w-full h-full items-center justify-center text-xl italic font-semibold text-red-600 loading-animation">{t('bad_token')}</div>;
};
const App = () => {

	const State = {
		UNLOADED:				0,
		MEDICAL_DATA_AVAILABLE:	1,
		TOKEN_UNINITIALIZED:	2,
		BAD_TOKEN:				3
	}

	const [state, setState]					= useState(State.UNLOADED);
	const [bearerName, setBearerName]		= useState('');
	const [isVerbal, setIsVerbal]			= useState(true);
	const [diseases, setDiseases]			= useState([]);
	const [birthYear, setBirthYear]			= useState(null);
	const [sex, setSex]						= useState(null);
	const [bloodType, setBloodType]			= useState(null);
	const [scanId, setScanId]				= useState(null);
	const hasFetched = useRef(false);

	const getTokenFromURL = () => {
		const searchParams = new URLSearchParams(location.search);
		return searchParams.get('token');
	}
	const getNoSmsFromURL = () => {
		const searchParams = new URLSearchParams(location.search);
		return searchParams.get('no_sms') === '1';
	}
	const token = getTokenFromURL();
	const noSms = getNoSmsFromURL();

	useEffect(() => {
		if (hasFetched.current) return;
		hasFetched.current = true;
		
		const fetchData = async () => {
			// Check if we've already scanned this token in this session
			const sessionKey = `tuutuum_scanned_${token}`;
			const alreadyScanned = sessionStorage.getItem(sessionKey) === 'true';
			
			// Add no_sms if already scanned (prevents duplicate SMS on refresh)
			const noSmsParam = alreadyScanned || noSms;
			
			// Always fetch fresh data from backend
			const bearer_data = await get_data_by_token(token, noSmsParam);
			if(bearer_data.success) {
				if(bearer_data?.data?.bearer_id) {
					setBearerName(bearer_data.data.name || '');
					setIsVerbal(bearer_data.data.is_verbal ?? true);
					setDiseases(bearer_data.data.diseases || []);
					setBirthYear(bearer_data.data.birth_year);
					setSex(bearer_data.data.sex);
					setBloodType(bearer_data.data.blood_type);
					setScanId(bearer_data.data.scan_id);
					setState(State.MEDICAL_DATA_AVAILABLE);
					
					// Mark as scanned for this session
					sessionStorage.setItem(sessionKey, 'true');
				} else {
					setState(State.TOKEN_UNINITIALIZED);
				}
			} else {
				setState(State.BAD_TOKEN);
			}
		}
		fetchData();
	}, []);

	const renderState = () => {
		switch(state) {
			case State.UNLOADED:
				return <Unloaded />;
				break;
			case State.MEDICAL_DATA_AVAILABLE:
				return <MedicalData
					token={token}
					bearerName={bearerName}
					isVerbal={isVerbal}
					diseases={diseases}
					birthYear={birthYear}
					sex={sex}
					bloodType={bloodType}
					scanId={scanId}
					noSms={noSms}
				/>;
				break;
			case State.TOKEN_UNINITIALIZED:
				return <UninitializedToken token={token} />;
				break;
			case State.BAD_TOKEN:
				return <BadToken token={token} />;
				break;
		}
	}

	return (
		<div>
			{renderState()}
		</div>
	);
}

window.addEventListener("DOMContentLoaded", function (e) {
	ReactDOM.createRoot(document.getElementById('root')).render(
		<React.StrictMode>
			<App />
		</React.StrictMode>
	  );
});
